import { useState } from "react";
import { useFormContext } from "react-hook-form";

import { AgentSelectionModal } from "src/agentNode/components/agentSelectionModal/AgentSelectionModal";
import { NoAgentSelected } from "src/agentNode/components/agentSelectionModal/NoAgentSelected";
import { SelectedAgentDisplay } from "src/agentNode/components/agentSelectionModal/SelectedAgentDisplay";
import { useAgent, useAgentVersion } from "src/agents/queries/management";
import { Card } from "src/base-components/Card";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { Agent, AgentVersion } from "src/clients/agent-management";
import { AgentNodeData } from "src/constants/NodeDataTypes";

type Props = {
  immutable: boolean;
  handleSelectAgent: (version: AgentVersion, agent: Agent) => void;
};

export const AgentSelectionEditor: React.FC<Props> = ({
  immutable,
  handleSelectAgent,
}) => {
  const { watch } = useFormContext<AgentNodeData>();
  const [modalOpen, setModalOpen] = useState(false);

  const agentId = watch("agent_id");
  const versionId = watch("agent_version_id");

  const agentQuery = useAgent(agentId ?? "");
  const versionQuery = useAgentVersion(agentId ?? "", versionId ?? "");

  const noAgentSelected = !agentId || !versionId;

  const renderContent = () => {
    if (noAgentSelected) {
      return (
        <NoAgentSelected
          handleSelectAgent={() => setModalOpen(true)}
          immutable={immutable}
        />
      );
    }

    if (agentQuery.isLoading || versionQuery.isLoading) {
      return (
        <Card>
          <SkeletonPlaceholder height="h-5" width="w-48" />
        </Card>
      );
    }

    if (!agentQuery.data || !versionQuery.data) {
      return (
        <NoAgentSelected
          handleSelectAgent={() => setModalOpen(true)}
          immutable={immutable}
        />
      );
    }

    return (
      <SelectedAgentDisplay
        agent={agentQuery.data}
        handleChangeAgent={() => setModalOpen(true)}
        immutable={immutable}
        version={versionQuery.data}
      />
    );
  };

  return (
    <>
      {renderContent()}
      <AgentSelectionModal
        handleClose={() => setModalOpen(false)}
        handleSubmit={handleSelectAgent}
        open={modalOpen}
      />
    </>
  );
};
